import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Mail, KeyRound } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [recoveryToken, setRecoveryToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [step, setStep] = useState("request"); // request -> reset -> done
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);

  const requestReset = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      if (data.recovery_token) {
        setRecoveryToken(data.recovery_token);
        setInfo("Generamos un código de recuperación. Copialo y creá una nueva contraseña.");
        setStep("reset");
      } else {
        setInfo(data.message || "Si el email existe, generamos una solicitud.");
        setStep("done");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^\d{4,12}$/.test(newPassword)) {
      setError("La nueva contraseña debe ser numérica (4 a 12 dígitos).");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token: recoveryToken, password: newPassword });
      setStep("done");
      setInfo("¡Listo! Ya podés iniciar sesión con tu nueva contraseña.");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-10">
      <div className="w-full max-w-md animate-fade-up">
        <div className="text-center mb-8">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-sage-500 text-white font-outfit text-lg mb-3">t</div>
          <h1 className="font-outfit text-3xl text-ink-900">Recuperar acceso</h1>
          <p className="font-dmsans text-sm text-ink-600 mt-1">No te preocupes, lo solucionamos.</p>
        </div>

        <div className="tk-card">
          {step === "request" && (
            <form onSubmit={requestReset} className="space-y-4" data-testid="forgot-form">
              <div>
                <label className="tk-label mb-1.5 block">Tu email</label>
                <div className="relative">
                  <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-600/60" />
                  <input
                    type="email"
                    className="tk-input pl-11"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="forgot-email"
                  />
                </div>
              </div>
              {error && <div className="text-sm text-wellness-critical bg-wellness-critical/5 border border-wellness-critical/20 rounded-2xl px-4 py-2.5">{error}</div>}
              <button type="submit" disabled={loading} className="tk-btn-primary w-full" data-testid="forgot-submit">
                {loading ? "Procesando…" : "Solicitar recuperación"}
              </button>
            </form>
          )}

          {step === "reset" && (
            <form onSubmit={submitReset} className="space-y-4" data-testid="reset-form">
              <div className="text-sm text-ink-600 bg-sage-50 border border-sage-100 rounded-2xl px-4 py-3" data-testid="reset-info">
                {info}
              </div>
              <div>
                <label className="tk-label mb-1.5 block">Código de recuperación</label>
                <input
                  className="tk-input font-mono text-xs"
                  value={recoveryToken}
                  onChange={(e) => setRecoveryToken(e.target.value)}
                  required
                  data-testid="reset-token"
                />
              </div>
              <div>
                <label className="tk-label mb-1.5 block">Nueva contraseña</label>
                <div className="relative">
                  <KeyRound size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-600/60" />
                  <input
                    type="password"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className="tk-input pl-11 tracking-[0.4em]"
                    placeholder="• • • •"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value.replace(/\D/g, ""))}
                    required
                    maxLength={12}
                    data-testid="reset-password"
                  />
                </div>
              </div>
              {error && <div className="text-sm text-wellness-critical bg-wellness-critical/5 border border-wellness-critical/20 rounded-2xl px-4 py-2.5">{error}</div>}
              <button type="submit" disabled={loading} className="tk-btn-primary w-full" data-testid="reset-submit">
                {loading ? "Guardando…" : "Actualizar contraseña"}
              </button>
            </form>
          )}

          {step === "done" && (
            <div className="space-y-4 text-center" data-testid="forgot-done">
              <div className="mx-auto h-14 w-14 rounded-full bg-sage-100 flex items-center justify-center text-sage-600">✓</div>
              <p className="font-dmsans text-ink-900">{info}</p>
              <Link to="/login" className="tk-btn-primary w-full" data-testid="forgot-back-login">
                Ir al inicio de sesión
              </Link>
            </div>
          )}

          <div className="pt-4 text-center">
            <Link to="/login" className="text-sm text-ink-600 hover:text-sage-600" data-testid="forgot-cancel">
              ← Volver
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
